﻿import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react'
import { Link } from 'react-router-dom'
import { Text, Button, Heading } from '../ui'
import {
  DocumentTextIcon,
  ChevronRightIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline'
import { getNewsLikesSummary } from '../../services/informativo'
import { InformativoVersion, NewsLikeSummary } from '../../types/informativo'
import DashboardSectionHeader from './DashboardSectionHeader'
import { DashboardCardSkeleton } from './DashboardSkeleton'
import NewsLikeButton from './NewsLikeButton'
import { useAuthContext } from '../../contexts/AuthContext'
import { modulesService } from '../../services/modules'
import type { ModuleWithPermissions } from '../../types'
import { getModuleIcon } from '../../utils/moduleIcons'
import clsx from 'clsx'

type InformativoTab = 'interno' | 'imprensa' | 'boletim'

interface TabState {
  versions: InformativoVersion[]
  loading: boolean
  error: string | null
}

interface InformativosHubProps {
  embedded?: boolean
  interno: TabState
  imprensa: TabState
  boletim: TabState
  formatDescription: (description: string) => string
  onInternoClick: (version: InformativoVersion) => void
  onBoletimClick: (version: InformativoVersion) => void
  onVersionClick: (file: string | null, link: string | null, title?: string) => void
}

const TAB_MODULE_NAMES: Record<InformativoTab, string> = {
  imprensa: 'informativo_imprensa',
  interno: 'informativo_interno',
  boletim: 'informativo_boletim',
}

const TAB_CONFIG: { id: InformativoTab; label: string; route: string }[] = [
  { id: 'imprensa', label: 'Últimas Notícias', route: '/informativo/imprensa' },
  { id: 'interno', label: 'Internos', route: '/informativo/interno' },
  { id: 'boletim', label: 'Boletim de Evoluções do QG', route: '/informativo/boletim' },
]

const AUTO_ROTATE_INTERVAL_MS = 20_000

const tabHasContent = (state: TabState) =>
  !state.loading && !state.error && state.versions.length > 0

const formatPublishedDate = (date: string) =>
  new Date(`${date}T12:00:00`).toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })

const formatEditionDate = (date: string) =>
  new Date(`${date}T12:00:00`).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })

const patternBg =
  "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"

const HERO_GRADIENT = 'linear-gradient(135deg, #115e59 0%, #008B95 60%, #2dd4bf 100%)'

const tabButtonClass = (active: boolean) =>
  clsx(
    'inline-flex items-center gap-1.5 py-2 px-1 border-b-2 font-medium text-sm transition-colors outline-none focus:outline-none data-[focus]:outline-none',
    active
      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
  )

const actionLabelFor = (version: InformativoVersion, opensDetail: boolean) => {
  if (opensDetail) return 'Ler mais'
  return version.file ? 'Abrir PDF' : 'Abrir link'
}

/** Extrai texto puro da descrição (HTML ou plain) para o mesmo estilo visual em todas as abas. */
const toPlainDescription = (description: string, formatDescription: (d: string) => string) => {
  if (!description) return ''
  const source = description.includes('<') ? formatDescription(description) : description
  return source
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/p>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, ' ')
    .trim()
}

const bodyDescriptionClass = 'text-gray-600 dark:text-gray-300 leading-relaxed'

interface VersionCardProps {
  version: InformativoVersion
  badgeLabel: string
  featured?: boolean
  noHeader?: boolean
  isInterno: boolean
  isBoletim: boolean
  formatDescription: (description: string) => string
  likeSummary: NewsLikeSummary
  onLikeChanged: (newsId: string, next: NewsLikeSummary) => void
  onInternoClick: (version: InformativoVersion) => void
  onBoletimClick: (version: InformativoVersion) => void
  onVersionClick: (file: string | null, link: string | null, title?: string) => void
}

const VersionCard: React.FC<VersionCardProps> = ({
  version,
  badgeLabel,
  featured = false,
  noHeader = false,
  isInterno,
  isBoletim,
  formatDescription,
  likeSummary,
  onLikeChanged,
  onInternoClick,
  onBoletimClick,
  onVersionClick,
}) => {
  const opensDetail = isInterno || isBoletim
  const actionLabel = actionLabelFor(version, opensDetail)
  const plainDescription = toPlainDescription(version.description || '', formatDescription)
  const newsId = version.id || ''
  const likeButton =
    newsId ? (
      <NewsLikeButton
        newsId={newsId}
        summary={likeSummary}
        onChanged={onLikeChanged}
      />
    ) : null

  const handleClick = () => {
    if (isInterno) {
      onInternoClick(version)
    } else if (isBoletim) {
      onBoletimClick(version)
    } else {
      onVersionClick(version.file, version.link, version.title)
    }
  }

  if (featured) {
    return (
      <article className="overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-md">
        <div
          className="relative flex items-center overflow-hidden px-4 py-3 sm:px-5 sm:py-3.5"
          style={{ background: HERO_GRADIENT }}
        >
          <div
            className="pointer-events-none absolute inset-0"
            style={{ backgroundImage: patternBg }}
            aria-hidden
          />
          <div className="relative flex w-full min-w-0 items-center justify-between gap-3">
            <div className="min-w-0 flex-1 flex flex-col gap-1.5 sm:gap-2">
              {version.title && (
                <Heading
                  as="h3"
                  size="xl"
                  className="!text-white text-[1.15rem] sm:text-xl font-extrabold leading-snug tracking-tight drop-shadow-sm line-clamp-3"
                >
                  {version.title}
                </Heading>
              )}
            </div>
            <div className="flex flex-shrink-0 items-center gap-2">
              {likeButton}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleClick}
                className="flex-shrink-0 bg-white ring-white/40 hover:bg-gray-100 !px-3.5 !py-1.5 text-xs font-semibold"
              >
                {actionLabel}
              </Button>
            </div>
          </div>
        </div>

        <div className="px-5 py-4 sm:px-6">
          {plainDescription ? (
            <Text size="sm" className={clsx(bodyDescriptionClass, 'line-clamp-4')}>
              {plainDescription}
            </Text>
          ) : null}
          <Text size="xs" className="text-gray-400 dark:text-gray-500 mt-3">
            Publicado em {formatPublishedDate(version.date)}
          </Text>
        </div>
      </article>
    )
  }

  if (noHeader) {
    return (
      <article className="overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
        <div className="px-4 py-4 sm:px-5">
          {version.title && (
            <p className="text-gray-900 dark:text-white text-sm font-semibold leading-snug mb-2 line-clamp-2">
              {version.title}
            </p>
          )}
          {plainDescription ? (
            <Text size="sm" className={clsx(bodyDescriptionClass, 'line-clamp-2 mb-3')}>
              {plainDescription}
            </Text>
          ) : null}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Text size="xs" className="text-gray-400 dark:text-gray-500">
              {formatEditionDate(version.date)}
            </Text>
            <div className="flex flex-shrink-0 items-center gap-2">
              {likeButton}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleClick}
                className="flex-shrink-0 bg-white ring-gray-200 hover:bg-gray-50 dark:ring-gray-600 !px-3 !py-1 text-xs font-semibold"
              >
                {actionLabel}
              </Button>
            </div>
          </div>
        </div>
      </article>
    )
  }

  return (
    <article className="overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
      <div
        className="relative flex items-center overflow-hidden px-4 py-2.5 sm:px-5"
        style={{ background: HERO_GRADIENT }}
      >
        <div
          className="pointer-events-none absolute inset-0"
          style={{ backgroundImage: patternBg }}
          aria-hidden
        />
        <div className="relative flex w-full min-w-0 items-center justify-between gap-3">
          <div className="min-w-0 flex-1 flex flex-col gap-1">
            <span className="inline-flex w-fit items-center rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white backdrop-blur-sm">
              {badgeLabel}
            </span>
            {version.title && (
              <p className="text-white text-sm font-bold leading-snug tracking-tight drop-shadow-sm line-clamp-2">
                {version.title}
              </p>
            )}
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            {likeButton}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleClick}
              className="flex-shrink-0 bg-white ring-white/40 hover:bg-gray-100 !px-3 !py-1 text-xs font-semibold"
            >
              {actionLabel}
            </Button>
          </div>
        </div>
      </div>

      <Text size="xs" className="text-gray-400 dark:text-gray-500 px-4 py-2.5 sm:px-5">
        {formatEditionDate(version.date)}
      </Text>
    </article>
  )
}

const emptyLike = (): NewsLikeSummary => ({ count: 0, likedByMe: false })

const VersionCardsLayout: React.FC<{
  versions: InformativoVersion[]
  badgeLabel: string
  isInterno: boolean
  isBoletim: boolean
  formatDescription: (description: string) => string
  likesByNewsId: Record<string, NewsLikeSummary>
  onLikeChanged: (newsId: string, next: NewsLikeSummary) => void
  onInternoClick: (version: InformativoVersion) => void
  onBoletimClick: (version: InformativoVersion) => void
  onVersionClick: (file: string | null, link: string | null, title?: string) => void
}> = ({
  versions,
  badgeLabel,
  isInterno,
  isBoletim,
  formatDescription,
  likesByNewsId,
  onLikeChanged,
  onInternoClick,
  onBoletimClick,
  onVersionClick,
}) => (
  <div className="flex flex-col gap-3">
    {versions.map((version, index) => (
      <VersionCard
        key={version.id ?? `${version.date}-${index}`}
        version={version}
        badgeLabel={badgeLabel}
        featured={index === 0}
        noHeader={index > 0}
        isInterno={isInterno}
        isBoletim={isBoletim}
        formatDescription={formatDescription}
        likeSummary={
          version.id ? likesByNewsId[version.id] || emptyLike() : emptyLike()
        }
        onLikeChanged={onLikeChanged}
        onInternoClick={onInternoClick}
        onBoletimClick={onBoletimClick}
        onVersionClick={onVersionClick}
      />
    ))}
  </div>
)

const TabPanelContent: React.FC<{
  tabLabel: string
  state: TabState
  isInterno: boolean
  isBoletim: boolean
  formatDescription: (description: string) => string
  likesByNewsId: Record<string, NewsLikeSummary>
  onLikeChanged: (newsId: string, next: NewsLikeSummary) => void
  onInternoClick: (version: InformativoVersion) => void
  onBoletimClick: (version: InformativoVersion) => void
  onVersionClick: (file: string | null, link: string | null, title?: string) => void
}> = ({
  tabLabel,
  state,
  isInterno,
  isBoletim,
  formatDescription,
  likesByNewsId,
  onLikeChanged,
  onInternoClick,
  onBoletimClick,
  onVersionClick,
}) => {
  if (state.loading) {
    return <DashboardCardSkeleton className="min-h-[16rem] rounded-2xl" />
  }

  if (state.error) {
    return (
      <div className="rounded-xl border border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20 p-4">
        <Text size="sm" className="text-yellow-800 dark:text-yellow-200">
          {state.error}
        </Text>
      </div>
    )
  }

  const versions = state.versions.slice(0, 3)
  if (versions.length === 0) {
    return (
      <Text size="sm" color="muted" className="py-4 text-center">
        Nenhum informativo publicado nesta categoria.
      </Text>
    )
  }

  return (
    <VersionCardsLayout
      versions={versions}
      badgeLabel={tabLabel}
      isInterno={isInterno}
      isBoletim={isBoletim}
      formatDescription={formatDescription}
      likesByNewsId={likesByNewsId}
      onLikeChanged={onLikeChanged}
      onInternoClick={onInternoClick}
      onBoletimClick={onBoletimClick}
      onVersionClick={onVersionClick}
    />
  )
}

const InformativosHub: React.FC<InformativosHubProps> = ({
  embedded = false,
  interno,
  imprensa,
  boletim,
  formatDescription,
  onInternoClick,
  onBoletimClick,
  onVersionClick,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const { profile } = useAuthContext()
  const [menuModules, setMenuModules] = useState<ModuleWithPermissions[]>([])
  const [likesByNewsId, setLikesByNewsId] = useState<Record<string, NewsLikeSummary>>({})

  const newsIdsKey = useMemo(() => {
    const ids = [
      ...interno.versions,
      ...imprensa.versions,
      ...boletim.versions,
    ]
      .map((v) => v.id)
      .filter((id): id is string => Boolean(id))
    return Array.from(new Set(ids)).sort().join(',')
  }, [interno.versions, imprensa.versions, boletim.versions])

  useEffect(() => {
    let cancelled = false
    const ids = newsIdsKey ? newsIdsKey.split(',') : []
    if (ids.length === 0) {
      setLikesByNewsId({})
      return
    }

    getNewsLikesSummary(ids)
      .then((summary) => {
        if (!cancelled) setLikesByNewsId(summary)
      })
      .catch(() => {
        if (!cancelled) setLikesByNewsId({})
      })

    return () => {
      cancelled = true
    }
  }, [newsIdsKey])

  const handleLikeChanged = useCallback((newsId: string, next: NewsLikeSummary) => {
    setLikesByNewsId((prev) => ({ ...prev, [newsId]: next }))
  }, [])

  useEffect(() => {
    let cancelled = false

    const loadMenuModules = async () => {
      if (!profile?.user_group_id) {
        if (!cancelled) setMenuModules([])
        return
      }

      const { data } = await modulesService.getActiveWithPermissionsByGroup(profile.user_group_id)
      if (!cancelled) setMenuModules(data ?? [])
    }

    loadMenuModules()
    return () => {
      cancelled = true
    }
  }, [profile?.user_group_id])

  const tabIcons = useMemo(() => {
    const icons: Record<InformativoTab, ReturnType<typeof getModuleIcon>> = {
      imprensa: getModuleIcon(),
      interno: getModuleIcon(),
      boletim: getModuleIcon(),
    }
    for (const tab of TAB_CONFIG) {
      const mod = menuModules.find((m) => m.name === TAB_MODULE_NAMES[tab.id])
      icons[tab.id] = getModuleIcon(mod?.icon)
    }
    return icons
  }, [menuModules])

  const states: Record<InformativoTab, TabState> = {
    interno,
    imprensa,
    boletim,
  }

  const visibleTabs = useMemo(
    () => TAB_CONFIG.filter((tab) => tabHasContent(states[tab.id])),
    [interno, imprensa, boletim]
  )

  const anyTabStillLoading = TAB_CONFIG.some((tab) => states[tab.id].loading)

  const loadingTab = useMemo(
    () => TAB_CONFIG.find((tab) => states[tab.id].loading),
    [interno, imprensa, boletim]
  )

  useEffect(() => {
    if (selectedIndex >= visibleTabs.length) {
      setSelectedIndex(0)
    }
  }, [visibleTabs.length, selectedIndex])

  useEffect(() => {
    if (visibleTabs.length <= 1) return

    const timer = window.setInterval(() => {
      setSelectedIndex((current) => (current + 1) % visibleTabs.length)
    }, AUTO_ROTATE_INTERVAL_MS)

    return () => window.clearInterval(timer)
  }, [visibleTabs.length])

  const hasAnyContent =
    interno.loading ||
    imprensa.loading ||
    boletim.loading ||
    interno.versions.length > 0 ||
    imprensa.versions.length > 0 ||
    boletim.versions.length > 0 ||
    interno.error ||
    imprensa.error ||
    boletim.error

  if (!hasAnyContent) return null

  return (
    <section
      className={clsx(
        'flex flex-col',
        embedded ? 'pt-0 pb-2 sm:pb-3 mb-0' : 'p-5 sm:p-6 mb-6'
      )}
    >
      <DashboardSectionHeader
        compact
        icon={<DocumentTextIcon className="h-5 w-5" />}
        title="Informativos"
      />

      {visibleTabs.length > 0 ? (
        <TabGroup selectedIndex={selectedIndex} onChange={setSelectedIndex}>
          <div className="mb-3 border-b border-gray-200 dark:border-gray-700">
            <TabList className="-mb-px flex flex-wrap gap-x-6 gap-y-1" aria-label="Informativos">
              {visibleTabs.map((tab) => {
                const TabIcon = tab.id === 'interno' ? BuildingOfficeIcon : tabIcons[tab.id]
                return (
                  <Tab key={tab.id} className={({ selected }) => tabButtonClass(selected)}>
                    <TabIcon className="h-4 w-4 shrink-0" aria-hidden />
                    {tab.label}
                  </Tab>
                )
              })}
            </TabList>
          </div>

          <TabPanels>
            {visibleTabs.map((tab) => (
              <TabPanel key={tab.id} className="outline-none focus:outline-none data-[focus]:outline-none">
                <TabPanelContent
                  tabLabel={tab.label}
                  state={states[tab.id]}
                  isInterno={tab.id === 'interno'}
                  isBoletim={tab.id === 'boletim'}
                  formatDescription={formatDescription}
                  likesByNewsId={likesByNewsId}
                  onLikeChanged={handleLikeChanged}
                  onInternoClick={onInternoClick}
                  onBoletimClick={onBoletimClick}
                  onVersionClick={onVersionClick}
                />
                {states[tab.id].versions.length > 3 && (
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                    <Link
                      to={tab.route}
                      className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
                    >
                      Ver todos
                      <ChevronRightIcon className="h-4 w-4" aria-hidden />
                    </Link>
                  </div>
                )}
              </TabPanel>
            ))}
          </TabPanels>
        </TabGroup>
      ) : anyTabStillLoading && loadingTab ? (
        <TabPanelContent
          tabLabel={loadingTab.label}
          state={states[loadingTab.id]}
          isInterno={loadingTab.id === 'interno'}
          isBoletim={loadingTab.id === 'boletim'}
          formatDescription={formatDescription}
          likesByNewsId={likesByNewsId}
          onLikeChanged={handleLikeChanged}
          onInternoClick={onInternoClick}
          onBoletimClick={onBoletimClick}
          onVersionClick={onVersionClick}
        />
      ) : null}
    </section>
  )
}

export default InformativosHub
